import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Phone, Mail, FileText, CheckCircle2, ArrowRight, ShieldCheck, Users, Star, Heart } from 'lucide-react';
import { Button } from '../components/Button';
export function ReferralPartnersPage() {
  return <div className="w-full" data-id="element-566">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-primary text-white px-6 relative overflow-hidden" data-id="element-567">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/10 -skew-x-12 translate-x-1/4" data-id="element-568" />
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-569">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl" data-id="element-570">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-display font-extrabold mb-6 md:mb-8" data-id="element-571">
              Referral Partners
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed" data-id="element-572">
              We partner with healthcare providers, case managers, and social
              service agencies to provide immediate, stable housing solutions
              for their clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Partner With */}
      <section className="py-20 md:py-32 px-6" data-id="element-573">
        <div className="max-w-7xl mx-auto text-center mb-12 md:mb-20" data-id="element-574">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal mb-4 md:mb-6" data-id="element-575">
            Who We Work With
          </h2>
          <p className="text-muted text-lg md:text-xl max-w-2xl mx-auto" data-id="element-576">
            We serve as a trusted housing resource for a wide range of
            professional organizations.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8" data-id="element-577">
          {[{
          name: 'Hospitals',
          icon: Building2
        }, {
          name: 'Behavioral Health',
          icon: ShieldCheck
        }, {
          name: 'Case Managers',
          icon: Users
        }, {
          name: 'Veterans Orgs',
          icon: Star
        }, {
          name: 'Probation',
          icon: FileText
        }, {
          name: 'Treatment Centers',
          icon: Heart
        }].map(partner => <div key={partner.name} className="flex flex-col items-center text-center space-y-4" data-id="element-578">
              <div className="w-20 h-20 bg-background rounded-2xl flex items-center justify-center text-primary shadow-sm border border-gray-100" data-id="element-579">
                {/* @ts-ignore */}
                <partner.icon size={32} data-id="element-580" />
              </div>
              <span className="font-bold text-charcoal text-sm" data-id="element-581">
                {partner.name}
              </span>
            </div>)}
        </div>
      </section>

      {/* Referral Process */}
      <section className="py-20 md:py-32 bg-background px-6" data-id="element-582">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-583">
          <div className="space-y-6 md:space-y-8" data-id="element-584">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal" data-id="element-585">
              Our Referral Process
            </h2>
            <p className="text-lg text-muted leading-relaxed" data-id="element-586">
              We understand that time is of the essence. Our streamlined process
              ensures your clients get the housing they need without the red
              tape.
            </p>

            <div className="space-y-12" data-id="element-587">
              {[{
              step: '01',
              title: 'Submit Referral',
              desc: 'Fill out our secure online referral form with basic client information.'
            }, {
              step: '02',
              title: 'Review Availability',
              desc: 'Our team reviews current bed availability and client eligibility within 24 hours.'
            }, {
              step: '03',
              title: 'Schedule Intake',
              desc: 'We coordinate with you to schedule a tour or intake interview for the client.'
            }, {
              step: '04',
              title: 'Move-In Day',
              desc: 'Client moves into their new home with our support team ready to welcome them.'
            }].map(item => <div key={item.step} className="flex gap-6" data-id="element-588">
                  <div className="text-4xl font-display font-black text-accent/30 shrink-0" data-id="element-589">
                    {item.step}
                  </div>
                  <div data-id="element-590">
                    <h4 className="text-xl font-bold text-charcoal mb-2" data-id="element-591">
                      {item.title}
                    </h4>
                    <p className="text-muted leading-relaxed" data-id="element-592">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="bg-white p-8 md:p-12 lg:p-16 rounded-3xl shadow-2xl border border-gray-100" data-id="element-593">
            <h3 className="text-2xl md:text-3xl font-display font-bold text-charcoal mb-6 md:mb-8" data-id="element-594">
              Submit a Referral
            </h3>
            <form className="space-y-6" data-id="element-595">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-596">
                <div className="space-y-2" data-id="element-597">
                  <label className="text-sm font-bold text-charcoal" data-id="element-598">
                    Agency Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-599" />
                </div>
                <div className="space-y-2" data-id="element-600">
                  <label className="text-sm font-bold text-charcoal" data-id="element-601">
                    Contact Person
                  </label>
                  <input type="text" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-602" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-603">
                <div className="space-y-2" data-id="element-604">
                  <label className="text-sm font-bold text-charcoal" data-id="element-605">
                    Phone
                  </label>
                  <input type="tel" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-606" />
                </div>
                <div className="space-y-2" data-id="element-607">
                  <label className="text-sm font-bold text-charcoal" data-id="element-608">
                    Email
                  </label>
                  <input type="email" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-609" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-610">
                <label className="text-sm font-bold text-charcoal" data-id="element-611">
                  Resident Information (Initials/ID)
                </label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-612" />
              </div>
              <div className="space-y-2" data-id="element-613">
                <label className="text-sm font-bold text-charcoal" data-id="element-614">
                  Desired Move-in Date
                </label>
                <input type="date" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-615" />
              </div>
              <div className="space-y-2" data-id="element-616">
                <label className="text-sm font-bold text-charcoal" data-id="element-617">
                  Additional Notes
                </label>
                <textarea rows={4} className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-618"></textarea>
              </div>
              <Button className="w-full py-4 text-lg" onClick={e => {
              e.preventDefault();
              alert('Referral submitted successfully!');
            }} data-id="element-619">
                Submit Referral
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>;
}